import { getFeatureFlag } from "@sinch/feature-flags";
import { setPortalConfig } from "../config/portalConfig";
import deDE from "../translations/de-DE.json";
import enUS from "../translations/en-US.json";
import esES from "../translations/es-ES.json";
import esMX from "../translations/es-MX.json";
import frFR from "../translations/fr-FR.json";
import itIT from "../translations/it-IT.json";
import ptBR from "../translations/pt-BR.json";

import type { Translations } from "./intl.types";

const localeToTranslationMap = {
  "de-DE": deDE,
  "en-US": enUS,
  "es-ES": esES,
  "es-MX": esMX,
  "fr-FR": frFR,
  "it-IT": itIT,
  "pt-BR": ptBR,
};

export type Locale = keyof typeof localeToTranslationMap;
const supportedLocales = Object.keys(localeToTranslationMap) as Array<Locale>;
const defaultLanguage = "en";
const defaultCountry = "US";

const withEnglishFallback = (other: Partial<Translations> = {}): Translations =>
  Object.assign({}, enUS, other);

export const localeToTranslations = (locale: Locale): Translations =>
  withEnglishFallback(localeToTranslationMap[locale]);

export const setWindowIntlMeta = (locale: Locale) => {
  const [language = defaultLanguage, country = defaultCountry] =
    locale.split("-");

  setPortalConfig("intlLocale", locale);
  setPortalConfig("intlLanguage", language);
  setPortalConfig("intlCountry", country);

  window.meta = {
    ...window.meta,
    intl: {
      locale,
      language,
      country,
    },
  };
};

export const matchLocaleToSupported = (locale: string) => {
  const fullMatch = supportedLocales.find(
    (supportedLocale) => supportedLocale === locale
  );
  if (fullMatch) return fullMatch;
  const partialMatch = supportedLocales.find((supportedLocale) =>
    supportedLocale.startsWith(locale.slice(0, 2))
  );
  if (partialMatch) return partialMatch;
  return "en-US";
};

export const getBrowserLang = (): Locale => {
  const browserLangEnabled = getFeatureFlag(
    "useBrowserLanguage",
    (x): x is boolean => typeof x === "boolean",
    true
  );
  if (browserLangEnabled) {
    const browserLocale = navigator.language;
    return matchLocaleToSupported(browserLocale);
  }

  return "en-US";
};
